/**
 * Advanced search utility functions for the Alumni application
 */

/**
 * Performs advanced search on alumni data with support for multiple search criteria
 * 
 * @param {Array} alumni - Array of alumni objects to search through
 * @param {String} searchTerm - The search term entered by the user
 * @param {Object} options - Additional search options
 * @returns {Array} - Filtered array of alumni matching the search criteria
 */
export const advancedSearch = (alumni, searchTerm, options = {}) => {
  if (!searchTerm || searchTerm.trim() === '') {
    return alumni;
  }

  // Convert search term to lowercase for case-insensitive matching
  const term = searchTerm.trim().toLowerCase();
  
  // Check if the search term has specific prefixes for targeted searching
  const isCompanySearch = term.startsWith('company:') || term.startsWith('c:');
  const isNameSearch = term.startsWith('name:') || term.startsWith('n:');
  const isPositionSearch = term.startsWith('position:') || term.startsWith('title:') || term.startsWith('p:') || term.startsWith('t:');
  const isEmailSearch = term.startsWith('email:') || term.startsWith('e:');
  const isPhoneSearch = term.startsWith('phone:') || term.startsWith('ph:');
  const isLinkedInSearch = term.startsWith('linkedin:') || term.startsWith('li:');
  
  // Extract the actual search value without the prefix
  let searchValue = term;
  if (isCompanySearch) {
    searchValue = term.replace(/^(company:|c:)\s*/, '');
  } else if (isNameSearch) {
    searchValue = term.replace(/^(name:|n:)\s*/, '');
  } else if (isPositionSearch) {
    searchValue = term.replace(/^(position:|title:|p:|t:)\s*/, '');
  } else if (isEmailSearch) {
    searchValue = term.replace(/^(email:|e:)\s*/, '');
  } else if (isPhoneSearch) {
    searchValue = term.replace(/^(phone:|ph:)\s*/, '');
  } else if (isLinkedInSearch) {
    searchValue = term.replace(/^(linkedin:|li:)\s*/, '');
  }

  // Return filtered alumni based on search criteria
  return alumni.filter(alumnus => {
    // If no alumnus data, skip
    if (!alumnus) return false;
    
    // Handle specific search types
    if (isCompanySearch) {
      // Search in both previous and current company fields
      return (
        (alumnus.company && alumnus.company.toLowerCase().includes(searchValue)) ||
        (alumnus.currentCompany && alumnus.currentCompany.toLowerCase().includes(searchValue))
      );
    }
    
    if (isNameSearch) {
      // Search in first name, last name, and full name fields
      return (
        (alumnus.firstName && alumnus.firstName.toLowerCase().includes(searchValue)) ||
        (alumnus.lastName && alumnus.lastName.toLowerCase().includes(searchValue)) ||
        (alumnus.fullName && alumnus.fullName.toLowerCase().includes(searchValue)) ||
        (`${alumnus.firstName || ''} ${alumnus.lastName || ''}`.toLowerCase().includes(searchValue))
      );
    }
    
    if (isPositionSearch) {
      // Search in job title fields
      return (
        (alumnus.title && alumnus.title.toLowerCase().includes(searchValue)) ||
        (alumnus.currentTitle && alumnus.currentTitle.toLowerCase().includes(searchValue))
      );
    }
    
    if (isEmailSearch) {
      // Search in email fields
      return (
        (alumnus.email && alumnus.email.toLowerCase().includes(searchValue)) ||
        (alumnus.personalEmail && alumnus.personalEmail.toLowerCase().includes(searchValue))
      );
    }
    
    if (isPhoneSearch) {
      // Search in phone fields
      return (
        (alumnus.phone && alumnus.phone.toLowerCase().includes(searchValue)) ||
        (alumnus.mobilePhone && alumnus.mobilePhone.toLowerCase().includes(searchValue))
      );
    }
    
    if (isLinkedInSearch) {
      // Search in LinkedIn URL field
      return (
        alumnus.linkedInUrl && alumnus.linkedInUrl.toLowerCase().includes(searchValue)
      );
    }
    
    // If no specific prefix, perform a general search across all fields
    return Object.entries(alumnus).some(([key, value]) => {
      // Skip null/undefined values and non-string/non-number values
      if (value === null || value === undefined) return false;
      
      // Convert value to string for searching
      const valueStr = value.toString().toLowerCase();
      
      // Check if the field contains the search term
      return valueStr.includes(searchValue);
    });
  });
};

/**
 * Parses a complex search query with multiple conditions
 * Supports AND, OR operators and parentheses for grouping
 * 
 * @param {String} query - Complex search query
 * @returns {Object} - Parsed search query object
 */
export const parseComplexQuery = (query) => {
  // This is a placeholder for future implementation
  // Will support queries like: "name:John AND (company:Google OR company:Microsoft)"
  return {
    original: query,
    parsed: true,
    // Additional parsing logic would go here
  };
};

/**
 * Highlights search term matches in text
 * 
 * @param {String} text - The text to highlight
 * @param {String} searchTerm - The search term to highlight
 * @returns {String} - HTML with highlighted matches
 */
export const highlightMatches = (text, searchTerm) => {
  if (!text || !searchTerm || searchTerm.trim() === '') {
    return text;
  }
  
  // Extract the actual search value without any prefix
  let searchValue = searchTerm.trim().toLowerCase();
  const prefixes = ['company:', 'c:', 'name:', 'n:', 'position:', 'title:', 'p:', 't:', 'email:', 'e:', 'phone:', 'ph:', 'linkedin:', 'li:'];
  
  for (const prefix of prefixes) {
    if (searchValue.startsWith(prefix)) {
      searchValue = searchValue.replace(new RegExp(`^${prefix}\\s*`), '');
      break;
    }
  }
  
  if (!searchValue) return text;
  
  // Create a regex that's case insensitive
  const regex = new RegExp(`(${searchValue})`, 'gi');
  
  // Replace matches with highlighted version
  return text.replace(regex, '<mark>$1</mark>');
};
